import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, PhoneCall, ArrowRight, BellRing, Mail, Handshake } from 'lucide-react';
import { toast } from 'sonner';
import { salesApi, apiError } from '../salesApi';
import { useT, fmtMoney } from '../salesI18n';

export const FOLLOW_UP_DAYS = 7;

// Dias sem resposta de uma proposta (null = não se aplica). Mesma regra do backend.
export const followUpDays = (p, leadStatus) => {
  if (!p || ['won', 'lost'].includes(leadStatus)) return null;
  const ref = p.status === 'sent' ? (p.sent_at || p.created_at) : p.status === 'draft' && p.pdf_file_id ? (p.pdf_generated_at || p.created_at) : null;
  if (!ref) return null;
  if (p.followup_snoozed_until && new Date(p.followup_snoozed_until) > new Date()) return null;
  const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
  return days >= FOLLOW_UP_DAYS ? days : null;
};

export const useSnooze = (onDone) => {
  const { t } = useT();
  const [busy, setBusy] = useState(null);
  const snooze = async (proposalId) => {
    setBusy(proposalId);
    try {
      await salesApi.post(`/proposals/${proposalId}/followup-snooze`);
      toast.success(t.followUpSnoozed);
      onDone?.(proposalId);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  return { snooze, busy };
};

const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

export const FollowUpCard = ({ items = [], days = FOLLOW_UP_DAYS, onChange }) => {
  const { t, lang } = useT();
  const list = items;
  const { snooze, busy } = useSnooze((pid) => onChange?.(list.filter((f) => f.proposal_id !== pid)));

  if (!list.length) return null;
  return (
    <section className="rounded-3xl border border-amber-200/80 bg-amber-50/70 backdrop-blur-xl shadow-sm p-4 sm:p-6 sales-rise" data-testid="followup-card">
      <div className="flex items-start gap-3 mb-4">
        <span className="h-10 w-10 rounded-2xl bg-amber-400 text-white flex items-center justify-center shrink-0 shadow-sm"><BellRing className="h-5 w-5" /></span>
        <div className="min-w-0">
          <h2 className="font-display font-bold text-base sm:text-lg text-slate-900">{t.followUpTitle} <span className="ml-1 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold h-6 min-w-6 px-2 tabular-nums" data-testid="followup-count">{list.length}</span></h2>
          <p className="text-xs text-slate-600">{t.followUpHint(days)}</p>
        </div>
      </div>
      <ul className="space-y-2">
        {list.map((f) => (
          <li key={f.proposal_id} className="rounded-2xl bg-white/80 border border-white/90 p-3 flex flex-wrap items-center gap-3" data-testid={`followup-item-${f.proposal_id}`}>
            <span className="h-9 w-9 shrink-0 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center">{initials(f.lead_name)}</span>
            <div className="min-w-[58%] sm:min-w-0 flex-1">
              <Link to={`/leads/${f.lead_id}?tab=proposal`} className="font-semibold text-sm text-slate-900 hover:text-teal-700 truncate block" data-testid={`followup-lead-${f.proposal_id}`}>{f.lead_name}</Link>
              <p className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="font-semibold text-slate-700">{f.number}</span>
                <span className="tabular-nums">{fmtMoney(f.total, lang)}</span>
                <span className="inline-flex items-center gap-1">{f.channel === 'email' ? <Mail className="h-3 w-3" /> : <Handshake className="h-3 w-3" />}{f.channel === 'email' ? t.sentByEmail : t.shownInPerson}</span>
                {f.assigned_to_name && <span className="hidden sm:inline">· {f.assigned_to_name}</span>}
              </p>
            </div>
            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap" data-testid={`followup-days-${f.proposal_id}`}>{t.daysNoReply(f.days)}</span>
            <div className="flex items-center gap-1.5 ml-auto">
              {f.phone && <a href={`tel:${f.phone}`} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold shadow-sm transition-colors" data-testid={`followup-call-${f.proposal_id}`}><Phone className="h-3.5 w-3.5" /> {t.call}</a>}
              <button type="button" onClick={() => snooze(f.proposal_id)} disabled={busy === f.proposal_id} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-amber-300 bg-white/80 text-amber-900 text-xs font-semibold hover:bg-amber-100 transition-colors disabled:opacity-60" data-testid={`followup-snooze-${f.proposal_id}`}><PhoneCall className="h-3.5 w-3.5" /> {t.calledSnooze}</button>
              <Link to={`/leads/${f.lead_id}?tab=proposal`} className="h-9 w-9 rounded-full border border-slate-200 bg-white/80 text-slate-600 hover:text-teal-700 hover:border-teal-200 flex items-center justify-center transition-colors" aria-label={t.openLead} data-testid={`followup-open-${f.proposal_id}`}><ArrowRight className="h-4 w-4" /></Link>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};
